import React, { useState } from "react";
import axiosInstance from "../../../../axiosConfig";
import { useNavigate } from "react-router-dom";

const MAX_GROUPS = 3;
const MAX_BANNERS_PER_GROUP = 4;

const BannerForm = () => {
  const navigate = useNavigate();

  // ---------- Factories ----------
  const createEmptyBannerContent = () => ({
    alt: "",
    isClickable: 0,
    clickLink: "",
    displayOrder: 1,
    validFrom: "",
    validTill: "",
    imageFile: null,
    mobileImageFile: null,
  });

  const createEmptyBanner = () => ({
    mainTitle: "",
    subTitle: "",
    shortDescription: "",
    description: "",
    groupOrder: 1,
    validFrom: "",
    validTill: "",
    isActionButtonEnable: 0,
    actionButtonModal: {
      text: "",
      hrefLink: "",
      isNewTab: 0,
      validFrom: "",
      validTill: "",
    },
    bannerContents: [createEmptyBannerContent()],
  });

  const createEmptyBannerGroup = () => ({
    displayOrder: 1,
    alignOrder: "HORIZONTAL", 
    banners: [createEmptyBanner()],
  });

  const initialPayload = {
    pageCode: "HOME",
    name: "",
    displayOrder: 1,
    blockHeader: "",
    subHeader: "",
    blockContent: "HomeBanner",
    link: "",
    validFrom: "",
    validTill: "",
    isPublic: 1,

    bannerGroups: [createEmptyBannerGroup()],
  };

  const [payload, setPayload] = useState(initialPayload);

  // ---------- Helpers ----------
  const setTopField = (name, value) => setPayload((p) => ({ ...p, [name]: value }));
  const setTopNumberField = (name, value) => {
    const n = value === "" ? "" : Number(value);
    setPayload((p) => ({ ...p, [name]: n }));
  };

  const setGroupField = (groupIndex, name, value) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      groups[groupIndex] = { ...groups[groupIndex], [name]: value };
      return { ...p, bannerGroups: groups };
    });
  };

  const setBannerField = (groupIndex, bannerIndex, name, value) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const banners = [...groups[groupIndex].banners];

      const nextBanner = { ...banners[bannerIndex], [name]: value };

      // if action button disabled -> clear modal fields (safe)
      if (name === "isActionButtonEnable" && Number(value) === 0) {
        nextBanner.actionButtonModal = {
          text: "",
          hrefLink: "",
          isNewTab: 0,
          validFrom: "",
          validTill: "",
        };
      }

      banners[bannerIndex] = nextBanner;
      groups[groupIndex] = { ...groups[groupIndex], banners };
      return { ...p, bannerGroups: groups };
    });
  };

  const setActionButtonField = (groupIndex, bannerIndex, name, value) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const banners = [...groups[groupIndex].banners];
      const banner = { ...banners[bannerIndex] };

      banner.actionButtonModal = { ...banner.actionButtonModal, [name]: value };
      banners[bannerIndex] = banner;
      groups[groupIndex] = { ...groups[groupIndex], banners };
      return { ...p, bannerGroups: groups };
    });
  };

  const setContentField = (groupIndex, bannerIndex, contentIndex, name, value) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const banners = [...groups[groupIndex].banners];
      const banner = { ...banners[bannerIndex] };
      const contents = [...banner.bannerContents];

      contents[contentIndex] = { ...contents[contentIndex], [name]: value };
      banner.bannerContents = contents;

      banners[bannerIndex] = banner;
      groups[groupIndex] = { ...groups[groupIndex], banners };
      return { ...p, bannerGroups: groups };
    });
  };

  // ---------- Group actions ----------
  const addBannerGroup = () => {
    setPayload((p) => {
      if (p.bannerGroups.length >= MAX_GROUPS) return p;
      return { ...p, bannerGroups: [...p.bannerGroups, createEmptyBannerGroup()] };
    });
  };

  const removeBannerGroup = (groupIndex) => {
    setPayload((p) => {
      const nextGroups = p.bannerGroups.filter((_, i) => i !== groupIndex);
      return { ...p, bannerGroups: nextGroups.length ? nextGroups : p.bannerGroups };
    });
  };

  // ---------- Banner actions ----------
  const addBanner = (groupIndex) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const group = groups[groupIndex];
      if (!group) return p;

      if (group.banners.length >= MAX_BANNERS_PER_GROUP) return p;

      groups[groupIndex] = { ...group, banners: [...group.banners, createEmptyBanner()] };
      return { ...p, bannerGroups: groups };
    });
  };

  const removeBanner = (groupIndex, bannerIndex) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const group = groups[groupIndex];
      if (!group) return p;

      const nextBanners = group.banners.filter((_, i) => i !== bannerIndex);
      if (!nextBanners.length) return p; // keep at least one banner per group

      groups[groupIndex] = { ...group, banners: nextBanners };
      return { ...p, bannerGroups: groups };
    });
  };

  // ---------- BannerContent actions ----------
  const addBannerContent = (groupIndex, bannerIndex) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const banners = [...groups[groupIndex].banners];
      const banner = { ...banners[bannerIndex] };

      banner.bannerContents = [...banner.bannerContents, createEmptyBannerContent()];

      banners[bannerIndex] = banner;
      groups[groupIndex] = { ...groups[groupIndex], banners };
      return { ...p, bannerGroups: groups };
    });
  };

  const removeBannerContent = (groupIndex, bannerIndex, contentIndex) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const banners = [...groups[groupIndex].banners];
      const banner = { ...banners[bannerIndex] };

      const contents = banner.bannerContents.filter((_, i) => i !== contentIndex);
      if (!contents.length) return p; // keep at least 1

      banner.bannerContents = contents;

      banners[bannerIndex] = banner;
      groups[groupIndex] = { ...groups[groupIndex], banners };
      return { ...p, bannerGroups: groups };
    });
  };

  // ---------- Date formatter ----------
  const toApiDateTime = (value, withSeconds = false) => {
    if (!value) return "";
    const base = value.replace("T", " ");
    return withSeconds ? `${base}:00` : base;
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();

      const appendValue = (key, value) => {
        const safe = value === null || value === undefined ? "" : value;
        formData.append(key, safe);
      };

      // Top level
      appendValue("pageCode", payload.pageCode);
      appendValue("name", payload.name);
      appendValue("displayOrder", payload.displayOrder);
      appendValue("blockHeader", payload.blockHeader);
      appendValue("subHeader", payload.subHeader);
      appendValue("blockContent", payload.blockContent);
      appendValue("link", payload.link);
      appendValue("validFrom", toApiDateTime(payload.validFrom));
      appendValue("validTill", toApiDateTime(payload.validTill));
      appendValue("isPublic", payload.isPublic);

      // bannerGroups (NOW includes displayOrder + alignOrder + multiple banners)
      payload.bannerGroups.forEach((group, gi) => {
        appendValue(`bannerGroups[${gi}][displayOrder]`, group.displayOrder);
        appendValue(`bannerGroups[${gi}][alignOrder]`, group.alignOrder);

        group.banners.forEach((banner, bi) => {
          appendValue(`bannerGroups[${gi}][banners][${bi}][mainTitle]`, banner.mainTitle);
          appendValue(`bannerGroups[${gi}][banners][${bi}][subTitle]`, banner.subTitle);
          appendValue(`bannerGroups[${gi}][banners][${bi}][shortDescription]`, banner.shortDescription);
          appendValue(`bannerGroups[${gi}][banners][${bi}][description]`, banner.description);
          appendValue(`bannerGroups[${gi}][banners][${bi}][groupOrder]`, banner.groupOrder);
          appendValue(`bannerGroups[${gi}][banners][${bi}][validFrom]`, toApiDateTime(banner.validFrom));
          appendValue(`bannerGroups[${gi}][banners][${bi}][validTill]`, toApiDateTime(banner.validTill));
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][isActionButtonEnable]`,
            banner.isActionButtonEnable
          );

          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][text]`,
            banner.actionButtonModal.text
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][hrefLink]`,
            banner.actionButtonModal.hrefLink
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][isNewTab]`,
            banner.actionButtonModal.isNewTab
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][validFrom]`,
            toApiDateTime(banner.actionButtonModal.validFrom)
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][validTill]`,
            toApiDateTime(banner.actionButtonModal.validTill)
          );

          banner.bannerContents.forEach((c, ci) => {
            appendValue(`bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][alt]`, c.alt);
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][isClickable]`,
              c.isClickable
            );
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][clickLink]`,
              c.clickLink
            );
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][displayOrder]`,
              c.displayOrder
            );
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][validFrom]`,
              toApiDateTime(c.validFrom)
            );
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][validTill]`,
              toApiDateTime(c.validTill)
            );

            if (c.imageFile) {
              formData.append(
                `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][imageFile]`,
                c.imageFile
              );
            }
            if (c.mobileImageFile) {
              formData.append(
                `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][mobileImageFile]`,
                c.mobileImageFile
              );
            }
          });
        });
      });

      const res = await axiosInstance.post(
        "/internal-api/v1/client-web/cms/page/page-blocks/banner-group/new-group",
        formData
      );

      console.log("Response:", res.data);
      alert("Banner group created successfully!");
      setPayload(initialPayload);
      navigate("/LandingPage");
    } catch (err) {
      console.error("Error creating banner group:", err);
      alert("Error creating banner group. Please check the console for details.");
    }
  };

  // ---------- UI ----------
  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <h2 className="mb-4 text-xl font-semibold text-gray-800">Create Banner Group</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Top-level */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            value={payload.name}
            onChange={(e) => setTopField("name", e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Display Order</label>
          <input
            type="number"
            value={payload.displayOrder}
            onChange={(e) => setTopNumberField("displayOrder", e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            min={1}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Block Header</label>
          <input
            type="text"
            value={payload.blockHeader}
            onChange={(e) => setTopField("blockHeader", e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Block Sub Header</label>
          <input
            type="text"
            value={payload.subHeader}
            onChange={(e) => setTopField("subHeader", e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Link</label>
          <input
            type="text"
            value={payload.link}
            onChange={(e) => setTopField("link", e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Valid From</label>
            <input
              type="datetime-local"
              value={payload.validFrom}
              onChange={(e) => setTopField("validFrom", e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Valid Till</label>
            <input
              type="datetime-local"
              value={payload.validTill}
              onChange={(e) => setTopField("validTill", e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Is Public</label>
          <select
            value={payload.isPublic}
            onChange={(e) => setTopNumberField("isPublic", Number(e.target.value))}
            className="w-full p-2 border border-gray-300 rounded-md"
          >
            <option value={1}>Yes</option>
            <option value={0}>No</option>
          </select>
        </div>

        {/* Groups */}
        {payload.bannerGroups.map((group, gi) => (
          <div key={gi} className="p-4 border rounded-md">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-medium text-gray-800">Banner Group {gi + 1}</h3>

              {payload.bannerGroups.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeBannerGroup(gi)}
                  className="px-3 py-1 text-sm text-white bg-red-600 rounded-md"
                >
                  Remove Group
                </button>
              )}
            </div>

            {/* Group-level fields */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Display Order
                </label>
                <input
                  type="number"
                  value={group.displayOrder}
                  onChange={(e) => setGroupField(gi, "displayOrder", Number(e.target.value))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  min={1}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Align Order
                </label>
                <select
                  value={group.alignOrder}
                  onChange={(e) => setGroupField(gi, "alignOrder", e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="HORIZONTAL">Horizontal</option>
                  <option value="VERTICAL">Vertical</option>
                </select>
              </div>
            </div>

            {/* Banners in this group */}
            <div className="mt-4">
              <div className="flex items-center justify-between">
                <h4 className="text-base font-semibold text-gray-800">
                  Banners ({group.banners.length}/{MAX_BANNERS_PER_GROUP})
                </h4>

                <button
                  type="button"
                  onClick={() => addBanner(gi)}
                  disabled={group.banners.length >= MAX_BANNERS_PER_GROUP}
                  className={`px-3 py-1 text-sm text-white rounded-md ${
                    group.banners.length >= MAX_BANNERS_PER_GROUP ? "bg-gray-400" : "bg-indigo-600"
                  }`}
                >
                  Add Banner
                </button>
              </div>

              {group.banners.map((banner, bi) => (
                <div key={bi} className="p-4 mt-3 border rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-lg font-medium text-gray-800">
                      Banner {bi + 1}
                      {/* {gi + 1}. */}
                    </h4>

                    {group.banners.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBanner(gi, bi)}
                        className="px-3 py-1 text-sm text-white bg-red-600 rounded-md"
                      >
                        Remove Banner
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Main Title</label>
                      <input
                        type="text"
                        value={banner.mainTitle}
                        onChange={(e) => setBannerField(gi, bi, "mainTitle", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Sub Title</label>
                      <input
                        type="text"
                        value={banner.subTitle}
                        onChange={(e) => setBannerField(gi, bi, "subTitle", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Short Description
                      </label>
                      <input
                        type="text"
                        value={banner.shortDescription}
                        onChange={(e) => setBannerField(gi, bi, "shortDescription", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Group Order</label>
                      <input
                        type="number"
                        value={banner.groupOrder}
                        onChange={(e) => setBannerField(gi, bi, "groupOrder", Number(e.target.value))}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        min={1}
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={banner.description}
                      onChange={(e) => setBannerField(gi, bi, "description", e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Valid From</label>
                      <input
                        type="datetime-local"
                        value={banner.validFrom}
                        onChange={(e) => setBannerField(gi, bi, "validFrom", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Valid Till</label>
                      <input
                        type="datetime-local"
                        value={banner.validTill}
                        onChange={(e) => setBannerField(gi, bi, "validTill", e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Action Button Enable
                    </label>
                    <select
                      value={banner.isActionButtonEnable}
                      onChange={(e) =>
                        setBannerField(gi, bi, "isActionButtonEnable", Number(e.target.value))
                      }
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value={0}>Disabled</option>
                      <option value={1}>Enabled</option>
                    </select>
                  </div>

                  {banner.isActionButtonEnable === 1 && (
                    <div className="p-3 mt-3 border rounded-md">
                      <h4 className="mb-2 text-sm font-semibold text-gray-700">Action Button Modal</h4>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Text</label>
                          <input
                            type="text"
                            value={banner.actionButtonModal.text}
                            onChange={(e) => setActionButtonField(gi, bi, "text", e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">Href Link</label>
                          <input
                            type="text"
                            value={banner.actionButtonModal.hrefLink}
                            onChange={(e) =>
                              setActionButtonField(gi, bi, "hrefLink", e.target.value)
                            }
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Open In New Tab
                          </label>
                          <select
                            value={banner.actionButtonModal.isNewTab}
                            onChange={(e) =>
                              setActionButtonField(gi, bi, "isNewTab", Number(e.target.value))
                            }
                            className="w-full p-2 border border-gray-300 rounded-md"
                          >
                            <option value={0}>No</option>
                            <option value={1}>Yes</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Valid From</label>
                          <input
                            type="datetime-local"
                            value={banner.actionButtonModal.validFrom}
                            onChange={(e) =>
                              setActionButtonField(gi, bi, "validFrom", e.target.value)
                            }
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Valid Till</label>
                          <input
                            type="datetime-local"
                            value={banner.actionButtonModal.validTill}
                            onChange={(e) =>
                              setActionButtonField(gi, bi, "validTill", e.target.value)
                            }
                            className="w-full p-2 border border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Banner Contents */}
                  <div className="p-3 mt-4 border rounded-md">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-700">Banner Contents</h4>
                      {/* <button
                        type="button"
                        onClick={() => addBannerContent(gi, bi)}
                        className="px-3 py-1 text-sm text-white bg-blue-600 rounded-md"
                      >
                        Add Banner Content
                      </button> */}
                    </div>

                    {banner.bannerContents.map((c, ci) => (
                      <div key={ci} className="p-3 mt-3 border rounded-md">
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="text-sm font-medium text-gray-700">Content {ci + 1}</h5>
                          {banner.bannerContents.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeBannerContent(gi, bi, ci)}
                              className="px-2 py-1 text-xs text-white bg-red-600 rounded-md"
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Alt</label>
                            <input
                              type="text"
                              value={c.alt}
                              onChange={(e) => setContentField(gi, bi, ci, "alt", e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-md"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Display Order
                            </label>
                            <input
                              type="number"
                              value={c.displayOrder}
                              onChange={(e) =>
                                setContentField(gi, bi, ci, "displayOrder", Number(e.target.value))
                              }
                              className="w-full p-2 border border-gray-300 rounded-md"
                              min={1}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Is Clickable
                            </label>
                            <select
                              value={c.isClickable}
                              onChange={(e) =>
                                setContentField(gi, bi, ci, "isClickable", Number(e.target.value))
                              }
                              className="w-full p-2 border border-gray-300 rounded-md"
                            >
                              <option value={0}>No</option>
                              <option value={1}>Yes</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Click Link</label>
                            <input
                              type="text"
                              value={c.clickLink}
                              onChange={(e) =>
                                setContentField(gi, bi, ci, "clickLink", e.target.value)
                              }
                              className="w-full p-2 border border-gray-300 rounded-md"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Valid From</label>
                            <input
                              type="datetime-local"
                              value={c.validFrom}
                              onChange={(e) =>
                                setContentField(gi, bi, ci, "validFrom", e.target.value)
                              }
                              className="w-full p-2 border border-gray-300 rounded-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Valid Till</label>
                            <input
                              type="datetime-local"
                              value={c.validTill}
                              onChange={(e) =>
                                setContentField(gi, bi, ci, "validTill", e.target.value)
                              }
                              className="w-full p-2 border border-gray-300 rounded-md"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Image File</label>
                            <input
                              type="file"
                              onChange={(e) =>
                                setContentField(
                                  gi,
                                  bi,
                                  ci,
                                  "imageFile",
                                  e.target.files && e.target.files[0] ? e.target.files[0] : null
                                )
                              }
                              className="w-full p-2 border border-gray-300 rounded-md"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700">
                              Mobile Image File
                            </label>
                            <input
                              type="file"
                              onChange={(e) =>
                                setContentField(
                                  gi,
                                  bi,
                                  ci,
                                  "mobileImageFile",
                                  e.target.files && e.target.files[0] ? e.target.files[0] : null
                                )
                              }
                              className="w-full p-2 border border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Add Group */}
        <button
          type="button"
          onClick={addBannerGroup}
          disabled={payload.bannerGroups.length >= MAX_GROUPS}
          className={`px-4 py-2 text-white rounded-md ${
            payload.bannerGroups.length >= MAX_GROUPS ? "bg-gray-400" : "bg-indigo-600"
          }`}
        >
          Add Another Banner Group ({payload.bannerGroups.length}/{MAX_GROUPS})
        </button>

        <button type="submit" className="px-6 py-2 ml-2 text-white bg-green-600 rounded-md">
          Submit Banner Group
        </button>
      </form>
    </div>
  );
};

export default BannerForm;
